/**
 * The primary `ProjectStore`: IndexedDB via the `idb` promise wrapper.
 *
 * Chosen over `localStorage` because it is asynchronous (no main-thread stall
 * on a large document), has a far larger quota, and stores structured values
 * without a JSON round-trip on every keystroke-triggered autosave.
 */
import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';
import { BRANDING } from '@/config/branding';
import type { CreateProjectInput, ProjectStore } from './ProjectStore';
import type { ProjectRecord, ProjectSummary } from './types';
import { isProjectRecord } from './types';
import { asStorageError } from './errors';
import {
  buildDuplicate,
  buildRecord,
  cloneRecord,
  normalizeName,
  nowIso,
  summarize,
} from './records';

const DB_NAME = `${BRANDING.storagePrefix}-projects`;
const DB_VERSION = 1;
const STORE = 'projects';
const UPDATED_AT_INDEX = 'updatedAt';

interface ProjectsDb extends DBSchema {
  [STORE]: {
    key: string;
    value: ProjectRecord;
    indexes: { [UPDATED_AT_INDEX]: string };
  };
}

export function createIndexedDbStore(): ProjectStore {
  let connection: Promise<IDBPDatabase<ProjectsDb>> | null = null;

  function connect(): Promise<IDBPDatabase<ProjectsDb>> {
    if (connection) return connection;

    const opening = openDB<ProjectsDb>(DB_NAME, DB_VERSION, {
      upgrade(database) {
        if (database.objectStoreNames.contains(STORE)) return;
        const store = database.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex(UPDATED_AT_INDEX, UPDATED_AT_INDEX);
      },
      blocking() {
        // Another tab is upgrading the schema. Close so it is not stuck waiting;
        // the next call here reopens at the new version.
        void connection?.then((database) => database.close());
        connection = null;
      },
      terminated() {
        // The browser killed the connection (tab discarded, storage cleared).
        connection = null;
      },
    });

    // A failed open must not be cached, or a transient failure would poison the
    // store for the rest of the session.
    opening.catch(() => {
      if (connection === opening) connection = null;
    });

    connection = opening;
    return opening;
  }

  /** Single funnel so callers only ever see our two error types. */
  async function run<T>(
    operation: string,
    body: (database: IDBPDatabase<ProjectsDb>) => Promise<T>,
  ): Promise<T> {
    try {
      return await body(await connect());
    } catch (error) {
      throw asStorageError(error, operation);
    }
  }

  /**
   * Reads through the `updatedAt` index, then drops anything that no longer
   * matches the current shape — the object store is user-writable and outlives
   * app versions.
   */
  async function readAll(database: IDBPDatabase<ProjectsDb>): Promise<ProjectRecord[]> {
    const rows: unknown[] = await database.getAllFromIndex(STORE, UPDATED_AT_INDEX);
    return rows.filter(isProjectRecord).map(cloneRecord);
  }

  async function list(): Promise<ProjectSummary[]> {
    return run('list', async (database) => summarize(await readAll(database)));
  }

  async function get(id: string): Promise<ProjectRecord | null> {
    return run('get', async (database) => {
      const row: unknown = await database.get(STORE, id);
      return isProjectRecord(row) ? cloneRecord(row) : null;
    });
  }

  async function put(record: ProjectRecord): Promise<void> {
    await run('put', async (database) => {
      await database.put(STORE, cloneRecord(record));
    });
  }

  async function create(input: CreateProjectInput): Promise<ProjectRecord> {
    return run('create', async (database) => {
      const record = buildRecord(input);
      await database.put(STORE, record);
      return record;
    });
  }

  async function rename(id: string, name: string): Promise<ProjectRecord | null> {
    return run('rename', async (database) => {
      const transaction = database.transaction(STORE, 'readwrite');
      const row: unknown = await transaction.store.get(id);
      if (!isProjectRecord(row)) {
        await transaction.done;
        return null;
      }
      const updated: ProjectRecord = {
        ...cloneRecord(row),
        name: normalizeName(name),
        updatedAt: nowIso(),
      };
      await transaction.store.put(updated);
      await transaction.done;
      return updated;
    });
  }

  async function duplicate(id: string): Promise<ProjectRecord | null> {
    return run('duplicate', async (database) => {
      const transaction = database.transaction(STORE, 'readwrite');
      const rows: unknown[] = await transaction.store.getAll();
      const existing = rows.filter(isProjectRecord);
      const original = existing.find((record) => record.id === id);
      if (!original) {
        await transaction.done;
        return null;
      }
      const copy = buildDuplicate(
        original,
        existing.map((record) => record.name),
      );
      await transaction.store.put(copy);
      await transaction.done;
      return copy;
    });
  }

  async function remove(id: string): Promise<void> {
    await run('remove', async (database) => {
      await database.delete(STORE, id);
    });
  }

  async function search(query: string): Promise<ProjectSummary[]> {
    return run('search', async (database) => summarize(await readAll(database), query));
  }

  async function clear(): Promise<void> {
    await run('clear', async (database) => {
      await database.clear(STORE);
    });
  }

  return { list, get, put, create, rename, duplicate, remove, search, clear, kind: 'indexeddb' };
}
